export type Vec = { dx: number; dy: number };

export type Movement = {
  directions: Vec[]; // unit vectors
  maxDistance: number | "infinite";
};

export type PieceRole =
  | "goalkeeper"
  | "defender"
  | "midfielder"
  | "striker";

export interface PieceType {
  role: PieceRole;
  name: string; // human readable
  movement: Movement;
  shootingRange?: number | "infinite"; // optional: max tiles from ball to goal line along shot
}

export interface TeamPieceDef {
  type: PieceRole; // reference by role key
  start: { x: number; y: number };
}

export interface ShotZone {
  maxDistanceFromGoal: number;
  goalkeeperReach: number;
}

export interface ShotRules {
  zones: ShotZone[];
}

export const CLASSIC_RULESET_ID = "classic-13x20-gk-4-8-goal7-tournament-v1";

/**
 * The current default keeper ladder: reach grows by one every 4 squares from
 * the goal (saveZoneLength 4 in shared/matchSettings.ts — the two must agree).
 * Kept as an explicit table here because matchSettings imports this module,
 * so config cannot call saveZonesFromLength without a cycle.
 */
export const DEFAULT_GK_SAVE_ZONES: ShotZone[] = [
  { maxDistanceFromGoal: 4, goalkeeperReach: 0 },
  { maxDistanceFromGoal: 8, goalkeeperReach: 1 },
  { maxDistanceFromGoal: 12, goalkeeperReach: 2 },
  { maxDistanceFromGoal: 16, goalkeeperReach: 3 },
  { maxDistanceFromGoal: 999, goalkeeperReach: 4 },
];

/**
 * The earlier classic ladder (bands of three). Frozen for the current puzzle
 * ruleset: published puzzles were validated under these zones and must stay
 * solvable exactly as authored, so they deliberately do NOT follow the
 * defaults above.
 */
export const CLASSIC_GK_SAVE_ZONES: ShotZone[] = [
  { maxDistanceFromGoal: 3, goalkeeperReach: 0 },
  { maxDistanceFromGoal: 6, goalkeeperReach: 1 },
  { maxDistanceFromGoal: 9, goalkeeperReach: 2 },
  { maxDistanceFromGoal: 12, goalkeeperReach: 3 },
  { maxDistanceFromGoal: 999, goalkeeperReach: 4 },
];

/**
 * The previous classic ruleset's zones (goal 5 wide, box 7 wide). Frozen for
 * the legacy puzzle ruleset: published puzzles were validated under this
 * geometry and must stay solvable exactly as authored. (The defaultConfig at
 * puzzle-authoring time used 4/8/12/16 — not 5/10/15/20, which never shipped
 * as a match ruleset and briefly mis-froze these zones.)
 */
export const LEGACY_GK_SAVE_ZONES: ShotZone[] = [
  { maxDistanceFromGoal: 4, goalkeeperReach: 0 },
  { maxDistanceFromGoal: 8, goalkeeperReach: 1 },
  { maxDistanceFromGoal: 12, goalkeeperReach: 2 },
  { maxDistanceFromGoal: 16, goalkeeperReach: 3 },
  { maxDistanceFromGoal: 999, goalkeeperReach: 4 },
];

export type TurnMode =
  | "single-action" // one action: move piece OR pass
  | "adjacent-bonus" // move a piece; if ends adjacent to ball, also pass
  | "two-actions" // two actions, not two ball moves
  | "move-then-shoot"; // move a piece; if adjacent to ball, can shoot; otherwise turn ends

export interface RulesConfig {
  board: { width: number; height: number };
  boxes: {
    // rectangular keeper boxes occupying full width? We'll use centered boxes with width and height.
    width: number;
    height: number;
  };
  goals: {
    width: number; // goal mouth width centered behind the box
  };
  penaltyAreaRestriction: {
    // opposing pieces cannot enter keeper's box
    enabled: boolean;
  };
  ball: {
    flyOverOtherPlayers: boolean;
    flyOverShooter: boolean;
    playerCanStepOnBall: boolean;
  };
  globalPlayerMoveLimit: number | "infinite";
  pass: {
    directions: Vec[]; // usually queen-like (8 dirs)
    maxDistance: number | "infinite";
    forbidIntoOwnBox: boolean;
  };
  shooting: ShotRules;
  anyPieceCanPlayBall?: boolean; // move-then-shoot: any adjacent piece can play ball, not just mover
  offside?: boolean; // track offside positions after passes; offside pieces can't play ball
  ballRetrieval?: boolean; // after a pass, a friendly piece adjacent to the ball may reposition it to an adjacent empty tile
  turnMode: TurnMode;
  pieces: Record<PieceRole, PieceType>;
  scoring: {
    // Define how games end
    mode: "firstGoal" | "bestOf" | "firstTo" | "turnLimit" | "infinite" | "shootout";
    goals?: number; // used for bestOf (odd number) or firstTo
    turns?: number; // used for turnLimit: total ply or full turns? we'll use full turns limit
    shootoutTurnsPerPhase?: number; // used for "shootout": turns per player before GK zones widen (default 10)
    /**
     * turnLimit only: when set and the game is level at the turn limit, play continues
     * in sudden death. Every `turnsPerPhase` full turns the GK save zone boundaries
     * stretch one step (3/6/9/12 → 4/8/12/16 → 5/10/15/20 …) and the maximum shot
     * distance grows with them; the first goal wins.
     */
    extraTime?: {
      turnsPerPhase: number;
      /** End a still-level extra-time game after this displayed full turn. */
      maxTurns?: number;
      /** Start extra time from reset formations and a centre kickoff. */
      kickoffReset?: boolean;
      /** Award that kickoff to the side whose on-field pieces had advanced
       * farther from their own goal on average at the regulation whistle. */
      kickoffByAverageAdvance?: boolean;
    };
    /**
     * turnLimit only: play the game in two halves of `turns / 2` (rounded down).
     * At half time everyone returns to their kickoff formation and the team that
     * did NOT take the opening kickoff starts the second half. Both the half-time
     * and the full-time whistle wait while an open attack is in progress — the
     * ball in one team's half with no goal-side player adjacent. The whistle
     * blows once a defender reaches the ball or it is played into the other half.
     */
    halfTime?: boolean;
  };
  clock?: {
    enabled: boolean;
    baseTimeMs: number;      // starting time per player in ms (default 30_000 = 30s)
    incrementMs: number;     // time added per turn in ms (default 5_000 = 5s)
  };
  teamSetup: {
    teamA: TeamPieceDef[];
    teamB: TeamPieceDef[];
    ball: { x: number; y: number } | "chooseByNonStarting";
  };
}

export const Directions = {
  rook: [
    { dx: 1, dy: 0 },
    { dx: -1, dy: 0 },
    { dx: 0, dy: 1 },
    { dx: 0, dy: -1 },
  ],
  bishop: [
    { dx: 1, dy: 1 },
    { dx: 1, dy: -1 },
    { dx: -1, dy: 1 },
    { dx: -1, dy: -1 },
  ],
  queen(): Vec[] {
    // return new mutable array
    return [
      { dx: 1, dy: 0 },
      { dx: -1, dy: 0 },
      { dx: 0, dy: 1 },
      { dx: 0, dy: -1 },
      { dx: 1, dy: 1 },
      { dx: 1, dy: -1 },
      { dx: -1, dy: 1 },
      { dx: -1, dy: -1 },
    ];
  },
  king(): Vec[] {
    return [
      { dx: 1, dy: 0 },
      { dx: -1, dy: 0 },
      { dx: 0, dy: 1 },
      { dx: 0, dy: -1 },
      { dx: 1, dy: 1 },
      { dx: 1, dy: -1 },
      { dx: -1, dy: 1 },
      { dx: -1, dy: -1 },
    ];
  },
} as const;

export const defaultConfig: RulesConfig = {
  // Board longer than wide; 20 rows (y: 0 to 20)
  board: { width: 13, height: 20 },
  // Centered boxes; width odd, defenders must start outside (box height 4)
  boxes: { width: 9, height: 4 },
  // Goals behind the pitch, centered on box, 7 tiles wide
  goals: { width: 7 },
  penaltyAreaRestriction: { enabled: false },
  ball: {
    flyOverOtherPlayers: true,
    flyOverShooter: false,
    playerCanStepOnBall: false,
  },
  globalPlayerMoveLimit: 5,
  pass: {
    directions: Directions.queen(),
    maxDistance: 10,
    forbidIntoOwnBox: false,
  },
  shooting: {
    zones: DEFAULT_GK_SAVE_ZONES.map((zone) => ({ ...zone })),
  },
  turnMode: "two-actions",
  pieces: {
    goalkeeper: {
      role: "goalkeeper",
      name: "Goalkeeper",
    movement: { directions: Directions.king(), maxDistance: 1 },
    shootingRange: 1,
    },
    defender: {
      role: "defender",
      name: "Defender",
    movement: { directions: [...Directions.rook], maxDistance: "infinite" },
    shootingRange: "infinite",
    },
    midfielder: {
      role: "midfielder",
      name: "Midfielder",
    movement: { directions: Directions.queen(), maxDistance: "infinite" },
    shootingRange: "infinite",
    },
    striker: {
      role: "striker",
      name: "Striker",
    movement: { directions: [...Directions.bishop], maxDistance: "infinite" },
    shootingRange: "infinite",
    },
  },
  scoring: { mode: "infinite" },
  clock: { enabled: false, baseTimeMs: 30_000, incrementMs: 5_000 },
  teamSetup: {
    // Team A (Blue) defends y=20 (bottom) and attacks toward y=0 (top)
    teamA: [
      { type: "goalkeeper", start: { x: 6, y: 18 } },
      { type: "defender", start: { x: 4, y: 16 } },
      { type: "defender", start: { x: 8, y: 16 } },
      { type: "midfielder", start: { x: 6, y: 14 } },
      { type: "striker", start: { x: 4, y: 12 } },
      { type: "striker", start: { x: 8, y: 11 } },
    ],
    // Team B (Red) defends y=0 (top) and attacks toward y=20 (bottom)
    teamB: [
      { type: "goalkeeper", start: { x: 6, y: 1 } },
      { type: "defender", start: { x: 4, y: 3 } },
      { type: "defender", start: { x: 8, y: 3 } },
      { type: "midfielder", start: { x: 6, y: 5 } },
      { type: "striker", start: { x: 4, y: 8 } },
      { type: "striker", start: { x: 8, y: 7 } },
    ],
    // Ball on middle row
    ball: { x: 6, y: 10 },
  },
};

/**
 * Shootout preset: first goal wins; goalkeeper save zones widen every 10 turns per player,
 * making it progressively easier to score.
 * Phase 0 (turns 0-9): zone boundaries at multiples of 2 (tightest GK coverage)
 * Phase 1 (turns 10-19): multiples of 3
 * Phase 2 (turns 20-29): multiples of 4 (same as standard match)
 * … and so on, until eventually the GK has zero reach for all shots.
 */
export const shootoutConfig: RulesConfig = {
  ...defaultConfig,
  turnMode: "move-then-shoot",
  anyPieceCanPlayBall: true,
  offside: true,
  scoring: { mode: "shootout", shootoutTurnsPerPhase: 10 },
};
